import type { GradeMappingSchema } from '@repo/schemas/grade-scale/gradeScale';
import type { QuizBuilderSchema, QuizCreateSchema } from '@repo/schemas/quiz/quizBuilder';
import type { GroupPrivate } from '@repo/types/groups';
import type { MemberChanges } from '@repo/types/memberChanges';
import type { PaginationWithName, PaginationWithQuizFilters } from '@repo/types/pagination';
import type { FeedbackAnswerQuestion, QuestionExportAnswer } from '@repo/types/questions/Question';
import type { QuizAttemptPrivate, QuizAttemptPublic, QuizPrivate, QuizPublic } from '@repo/types/quiz';
import type { UserPublic } from '@repo/types/users';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';
export class QuizService {
  static async getQuiz(id?: string): Promise<AxiosResponse<QuizPrivate>> {
    return await axiosInstance.get(`/api/quiz/${id}`);
  }

  static async getQuizTeacher(id?: string): Promise<AxiosResponse<QuizDetailsForTeacherData>> {
    return await axiosInstance.get(`/api/quiz/${id}`);
  }

  static async getQuizPublic(id?: string): Promise<AxiosResponse<QuizPublic & { quizresps: Array<QuizAttemptPublic> }>> {
    return await axiosInstance.get(`/api/quiz/${id}/public`);
  }

  static async getQuizzes(params?: PaginationWithName): Promise<AxiosResponse<QuizPublic[]>> {
    return await axiosInstance.get('/api/quiz', { params });
  }

  static async getAssignedQuizzes(params?: PaginationWithQuizFilters): Promise<AxiosResponse<Array<QuizAssignedDataWithAttempts>>> {
    return await axiosInstance.get('/api/quiz/assigned', { params });
  }

  static async getAttempt(quizId: string, attemptId: string): Promise<AxiosResponse<QuizAttemptPrivate>> {
    return await axiosInstance.get(`/api/quiz/${quizId}/retrieve/${attemptId}`);
  }

  static async startQuiz(data: QuizStartData): Promise<AxiosResponse<string>> {
    return await axiosInstance.post(`/api/quiz/${data.quizId}/start`, {
      force: data.force,
    });
  }

  static async createQuiz(data: QuizCreateSchema): Promise<AxiosResponse<QuizBuilderData>> {
    return await axiosInstance.post('/api/quiz', data);
  }

  static async editQuiz(id: string, data: Partial<QuizBuilderSchema>): Promise<AxiosResponse<QuizBuilderData>> {
    return await axiosInstance.post(`/api/quiz/${id}/edit`, data);
  }

  static async deleteQuiz(data: DeleteQuizData): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete(`/api/quiz/${data.quizId}`, {
      data: { force: data.force },
    });
  }

  static async editQuizMembers(id: string, data: QuizMembersChangePayload): Promise<AxiosResponse<string>> {
    return await axiosInstance.post(`/api/quiz/${id}/members`, data);
  }

  static async saveQuiz(id: string, attemptId: string, data: QuizSaveData): Promise<AxiosResponse<string>> {
    return await axiosInstance.post(`/api/quiz/${id}/save/${attemptId}`, data);
  }

  static async submitQuiz(id: string, attemptId: string): Promise<AxiosResponse<QuizAttemptPrivate>> {
    return await axiosInstance.post(`/api/quiz/${id}/submit/${attemptId}`);
  }

  static async subSubmitQuiz(id: string, attemptId: string, data: Omit<QuizSubSubmitData, 'answers'>): Promise<AxiosResponse<QuizAttemptPrivate>> {
    return await axiosInstance.post(`/api/quiz/${id}/subsubmit/${attemptId}`, data);
  }

  static async getMarkedQuiz(id?: string, attemptId?: string): Promise<AxiosResponse<{ quiz: QuizPrivate; attempt: QuizAttemptPrivate }>> {
    return await axiosInstance.get(`/api/quiz/${id}/marked/${attemptId}`);
  }

  static async editMarkedQuiz(data: QuizEditMarkedData, id?: string, attemptId?: string): Promise<AxiosResponse<QuizAttemptPrivate>> {
    return await axiosInstance.post(`/api/quiz/${id}/marked/${attemptId}`, data);
  }

  static async deleteMarkedQuiz(data: DeleteQuizMarkedData): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete(`/api/quiz/${data.quizId}/marked/${data.attemptId}`);
  }

  static async uploadQuizMedia(quizId: string, file: File | string): Promise<AxiosResponse<QuizUploadMediaResponse>> {
    const form = new FormData();
    if (file instanceof File) {
      form.append('file', file);
    } else {
      form.append('fileId', file);
    }
    return await axiosInstance.post(`/api/quiz/${quizId}/uploadMedia`, form, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}

export interface QuizStartData {
  quizId: string;
  force?: boolean;
}

export interface DeleteQuizData {
  quizId: string;
  force?: boolean;
}

export interface DeleteQuizMarkedData {
  quizId: string;
  attemptId: string;
}

export interface QuizSubSubmitData {
  n?: number;
  timers: (number | null)[];
}

export interface QuizSaveData {
  answers: QuestionExportAnswer[][];
}

export interface QuizBuilderData extends QuizBuilderSchema {
  id: string;
}

export interface QuizAssignedDataWithAttempts extends QuizPublic {
  quizresps: QuizAttemptPublic[];
}

export interface QuizMembersChangePayload {
  memberChanges: MemberChanges[];
}

export interface QuizDetailsForTeacherData extends QuizPrivate {
  groups: GroupPrivate[];
  users: UserPublic[];
  quizresps: (QuizAttemptPublic & { user: UserPublic })[];
  gradeMapping: GradeMappingSchema | null;
}

export interface QuizUploadMediaResponse {
  id: string;
  name: string;
  fileType: string;
  creatorId: string;
  directoryId: string;
}

export interface QuizEditMarkedData {
  marked?: FeedbackAnswerQuestion[][];
  recalculateMarked?: boolean;
}
